import React, { useState, useEffect, useCallback, useRef } from "react";

// ------------ Lib ------------------
import _ from "lodash";

// ------------ Components (Material-UI v4) ------------
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  SnackbarContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import PhotoIcon from "@material-ui/icons/Photo";
import InfoIcon from "@material-ui/icons/Info";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloseIcon from "@material-ui/icons/Close";

// -------------- APIs ---------------
import API from "../../network/dashboardAPI";

// -------------- Services -----------
import UploadService from "../../services/FileUploadService";

const appPort = window.location.port.toString();
const appProtocol = window.location.protocol.toString();
const appHost = window.location.hostname.toString();
const appUrl = `${appProtocol}//${appHost}:${appPort}`;

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: "#f7f7f7",
    minHeight: "100vh",
    padding: theme.spacing(4),
    "@media (max-width: 989px)": { paddingTop: theme.spacing(9) },
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 12,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  subtitle: { color: "#64748b" },
  tabs: { borderBottom: "1px solid #e2e8f0", marginBottom: theme.spacing(2) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px`,
    flexWrap: "wrap",
  },
  search: { flex: 1, minWidth: 220 },
  statusFilter: { minWidth: 160 },
  headCell: {
    fontSize: 11,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#94a3b8",
    fontWeight: 600,
  },
  thumb: {
    width: 54,
    height: 40,
    borderRadius: 6,
    objectFit: "cover",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  emptyThumb: {
    width: 54,
    height: 40,
    borderRadius: 6,
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageCell: { display: "flex", alignItems: "center", gap: 8 },
  posBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    height: 28,
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    color: "#2563eb",
    fontWeight: 600,
    fontSize: 13,
  },
  description: { color: "#94a3b8", maxWidth: 320 },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: "#ECF8FF",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  infoHeader: { display: "flex", alignItems: "center", marginBottom: 4, fontWeight: 600 },
  uploadCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: theme.spacing(2),
    height: "100%",
    boxShadow: "none",
  },
  uploadHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    gap: 8,
  },
  preview: {
    minHeight: 220,
    borderRadius: 8,
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginTop: theme.spacing(1),
  },
  previewImg: { maxWidth: "100%", maxHeight: 320, objectFit: "contain" },
  hiddenInput: { display: "none" },
}));

const chip = {
  wide: { backgroundColor: "#f1f5f9", color: "#475569", fontWeight: 600 },
  required: { backgroundColor: "#fef3c7", color: "#b45309", fontWeight: 600 },
  optional: { backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600 },
  hero: { backgroundColor: "#dbeafe", color: "#1d4ed8", fontWeight: 600 },
  cms: { backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600 },
  cmsMuted: { backgroundColor: "#f1f5f9", color: "#94a3b8", fontWeight: 600 },
  active: { backgroundColor: "#dcfce7", color: "#166534", fontWeight: 600 },
  inactive: { backgroundColor: "#fee2e2", color: "#991b1b", fontWeight: 600 },
};

const SlideBanner = () => {
  const classes = useStyles();

  /* Refs */
  const _sliderImageInputFile = useRef(null);
  const _squareImageInputFile = useRef(null);
  const _searchRef = useRef(null);

  /* Requests */
  const [isRequesting, setIsRequesting] = useState(false);
  const [isRequestingUpsert, setIsRequestingUpsert] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingSquareImage, setIsUploadingSquareImage] = useState(false);

  /* Table */
  const [slideList, setSlideList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  /* Pagination */
  const [next, setNext] = useState(0);
  const [limit] = useState(10);
  const [pager, setPager] = useState(1);
  const [sliderCount, setSliderCount] = useState(0);

  /* Modals */
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowImageModal, setIsShowImageModal] = useState(false);
  const [imageUrlToView, setImageUrlToView] = useState("");

  /* Form */
  const [slideDataToUpdate, setSlideDataToUpdate] = useState({});
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [squareImageUrl, setSquareImageUrl] = useState("");
  const [webLink, setWebLink] = useState("");
  const [slideId, setSlideId] = useState("");
  const [slideNumber, setSlideNumber] = useState("");
  const [formType, setFormType] = useState("ADD");
  const [openNewTab, setOpenNewTab] = useState(0);

  const [formError, setFormError] = useState({
    title: false,
    description: false,
    imageUrl: false,
    webLink: false,
    slideNumber: false,
  });
  const [webLinkFormatError, setWebLinkFormatError] = useState(false);

  /* Toast */
  const [toastSeverity, setToastSeverity] = useState("success");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  /* Tabs */
  const [type, setType] = useState(0);
  const isProfile = type === 3;

  /* Confirm */
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [slideDataToUpdateStatus, setSlideDataToUpdateStatus] = useState({});
  const [isRequestingUpdateStatus, setIsRequestingUpdateStatus] = useState(false);

  /* ==================== Data ==================== */
  const getSlideList = async ($next) => {
    setIsRequesting(true);
    const params = { next: $next, limit };
    const query = { isDeleted: 0, type, search: searchQuery, isActive: selectedStatus };

    if (selectedStatus === 1 || selectedStatus === 0) {
      query.isActive = selectedStatus;
    }

    await API.getSliderList(params, query)
      .then((res) => res.json())
      .then((res) => {
        if (res.success === true && !_.isEmpty(res.data)) {
          setSlideList(res.data.list);
          setSliderCount(res.data.total);

          if (res.data.next) {
            setNext(res.data.next);
          } else if (res.data.list.length) {
            setNext($next > 0 ? next + res.data.list.length : res.data.list.length);
          } else {
            setNext(0);
          }
          setIsRequesting(false);
        } else {
          setSlideList([]);
          setSliderCount(0);
          setIsRequesting(false);
        }
      });
  };

  useEffect(() => {
    getSlideList(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, type, searchQuery]);

  /* ==================== Upsert ==================== */
  const updateSlide = async () => {
    setIsRequestingUpsert(true);
    const params = { slider_id: slideId };
    const body = {
      image_link: imageUrl,
      square_image_link: squareImageUrl || null,
      is_active: slideDataToUpdate.is_active,
      type,
      title,
      description,
      order: slideNumber,
      web_link: webLink,
      app_link: slideDataToUpdate.app_link,
      store_id: slideDataToUpdate.store_id,
      new_tab: openNewTab,
    };

    await API.updateSlide(params, body)
      .then((res) => res.json())
      .then((res) => {
        if (res.success === true && !_.isEmpty(res.data)) {
          const idx = slideList.findIndex((x) => x.slider_id === slideId);
          if (idx > -1) slideList[idx] = _.clone(res.data);
          getSlideList(0);
          handleShowToast("SUCCESS");
          setIsRequestingUpsert(false);
          handleCloseModal();
        } else {
          handleShowToast("ERROR");
          setIsRequestingUpsert(false);
        }
      });
  };

  const createSlide = async () => {
    setIsRequestingUpsert(true);
    const body = {
      image_link: imageUrl,
      square_image_link: squareImageUrl || null,
      is_active: 1,
      type,
      title,
      description,
      order: 1,
      web_link: webLink,
      is_deleted: 0,
      app_link: "",
      store_id: 1,
      new_tab: openNewTab,
    };

    await API.createSlide(body)
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          handleShowToast("SUCCESS");
          getSlideList(0);
        } else {
          handleShowToast("ERROR");
        }
        resetPagination();
        setIsRequestingUpsert(false);
        handleCloseModal();
      });
  };

  /* ==================== Form helpers ==================== */
  const setFormData = (banner) => {
    setSlideDataToUpdate(banner);
    setTitle(banner.title || "");
    setDescription(banner.description || "");
    setImageUrl(banner.image_link || "");
    setSquareImageUrl(banner.square_image_link || "");
    setSlideId(banner.slider_id);
    setWebLink(banner.web_link || "");
    setSlideNumber(banner.order);
    setOpenNewTab(banner.new_tab);
  };

  const resetFormData = () => {
    setSlideDataToUpdate({});
    setTitle("");
    setDescription("");
    setImageUrl("");
    setSquareImageUrl("");
    setSlideId("");
    setWebLink("");
    setSlideNumber("");
    setIsUploadingImage(false);
    setIsUploadingSquareImage(false);
    setOpenNewTab(0);
  };

  const resetPagination = () => {
    setPager(1);
  };

  const resetFormError = () => {
    setFormError({ title: false, description: false, imageUrl: false, webLink: false, slideNumber: false });
    setWebLinkFormatError(false);
  };

  const checkValidateWebLink = () => {
    const linkExpressValidate = webLink.includes(`${appUrl}/express`);
    const linkServer = webLink.includes(`${appUrl}`);
    if (type === 0) {
      setWebLinkFormatError(linkExpressValidate);
      return !linkExpressValidate;
    }
    setWebLinkFormatError(linkServer ? !linkExpressValidate : false);
    return linkServer ? linkExpressValidate : true;
  };

  const checkFormValidate = () => {
    const tmp = { title: false, description: false, imageUrl: false, webLink: false, slideNumber: false };
    if (!title) tmp.title = true;
    if (!description) tmp.description = true;
    // At least one image is required (rectangle hero OR square CMS image)
    if (!imageUrl && !squareImageUrl) tmp.imageUrl = true;
    if (!slideNumber && formType === "UPDATE") tmp.slideNumber = true;
    setFormError(tmp);

    let linkValid = true;
    if (webLink) linkValid = checkValidateWebLink();

    return Object.values(tmp).every((el) => el === false) && linkValid;
  };

  const handleUpsertSlider = async () => {
    if (checkFormValidate()) {
      if (slideId) await updateSlide();
      else await createSlide();
    }
  };

  /* ==================== Modal events ==================== */
  const handleOpenModal = (nextFormType, data) => {
    if (nextFormType === "UPDATE") setFormData(data);
    setFormType(nextFormType);
    setIsShowModal(true);
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
    resetFormData();
    resetFormError();
  };

  /* ==================== Field handlers ==================== */
  const handleChangeOpenNewTab = (value) => setOpenNewTab(value ? 1 : 0);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleDescription = (e) => setDescription(e.target.value);
  const handleChangeSlideNumber = (e) => setSlideNumber(e.target.value);
  const handleInputChange = (e) => setWebLink(e.target.value);

  /* ==================== Uploads ==================== */
  const uploadFromInput = async (inputRef, setUrl, setUploading) => {
    try {
      setUploading(true);
      if (inputRef.current && inputRef.current.files && inputRef.current.files[0]) {
        const formData = new FormData();
        formData.append("image", inputRef.current.files[0]);
        const response = await UploadService.uploadImageFromDashboard(formData);
        if (response.data.success && response.data.data.length) {
          handleShowToast("SUCCESS");
          setUrl(response.data.data[0].url);
        }
      }
      setUploading(false);
    } catch (e) {
      console.log("Debug: ", e);
      handleShowToast("ERROR");
      setUploading(false);
    }
  };

  const handleHeroUpload = () => uploadFromInput(_sliderImageInputFile, setImageUrl, setIsUploadingImage);
  const handleSquareUpload = () => uploadFromInput(_squareImageInputFile, setSquareImageUrl, setIsUploadingSquareImage);

  /* ==================== Toast ==================== */
  const handleShowToast = (kind) => {
    setShowToast(true);
    if (kind === "SUCCESS") {
      setToastSeverity("success");
      setToastMessage("Operation successful.");
    } else {
      setToastSeverity("error");
      setToastMessage("Operation error.");
    }
  };

  /* ==================== Pagination ==================== */
  const updatePaginationProps = (direction) => {
    let tmpPager = pager + 1;
    if (direction === "PREV") tmpPager = pager - 1;
    const tmpNext = limit * tmpPager;
    const tmpPrev = limit * tmpPager - limit;
    setPager(tmpPager);
    return { pager: tmpPager, next: tmpNext, prev: tmpPrev };
  };

  const handlePaginNext = () => {
    const { prev: p } = updatePaginationProps("NEXT");
    getSlideList(p);
  };

  const handlePaginPrev = () => {
    const { prev: p } = updatePaginationProps("PREV");
    getSlideList(p);
  };

  /* ==================== Tabs / filters / search ==================== */
  const handleTypeChange = (key) => {
    resetPagination();
    setSelectedStatus(1);
    setSearchQuery("");
    if (_searchRef.current) _searchRef.current.value = "";
    setType(parseInt(key, 10));
  };

  const handleChangeStatusFilter = (e) => {
    resetPagination();
    setSelectedStatus(e.target.value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchBanner = useCallback(
    _.debounce((value) => setSearchQuery(value), 750),
    []
  );

  /* ==================== Image modals ==================== */
  const handleShowImageModal = (url) => {
    if (!url) return;
    setImageUrlToView(url);
    setIsShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setImageUrlToView("");
    setIsShowImageModal(false);
  };

  /* ==================== Confirm active/inactive ==================== */
  const handleOpenConfirmModal = (slide) => {
    setIsShowConfirmModal(true);
    setSlideDataToUpdateStatus(slide);
  };

  const handleCloseConfirmModal = () => {
    setIsShowConfirmModal(false);
    setIsRequestingUpdateStatus(false);
    setSlideDataToUpdateStatus({});
  };

  const handleSubmitDisActive = async () => {
    setIsRequestingUpdateStatus(true);
    const params = { slider_id: slideDataToUpdateStatus.slider_id };
    const body = {
      image_link: slideDataToUpdateStatus.image_link,
      square_image_link: slideDataToUpdateStatus.square_image_link || null,
      is_active: slideDataToUpdateStatus.is_active === 1 ? 0 : 1,
      type: slideDataToUpdateStatus.type,
      title: slideDataToUpdateStatus.title,
      description: slideDataToUpdateStatus.description,
      order: slideDataToUpdateStatus.order,
      web_link: slideDataToUpdateStatus.web_link,
      app_link: slideDataToUpdateStatus.app_link,
      store_id: slideDataToUpdateStatus.store_id,
    };

    await API.updateSlide(params, body)
      .then((res) => res.json())
      .then((res) => {
        if (res.success === true && !_.isEmpty(res.data)) {
          handleShowToast("SUCCESS");
          handleCloseConfirmModal();
          getSlideList(0);
          resetPagination();
        } else {
          handleShowToast("ERROR");
          handleCloseConfirmModal();
        }
      });
  };

  /* ==================== Render helpers ==================== */
  const renderImageCell = (src, label, baseStyle, mutedStyle) => {
    const muted = !src;
    return (
      <div className={classes.imageCell}>
        {src ? (
          <img src={src} alt={label} className={classes.thumb} onClick={() => handleShowImageModal(src)} />
        ) : (
          <div className={classes.emptyThumb}>
            <PhotoIcon style={{ color: "#cbd5e1", fontSize: 20 }} />
          </div>
        )}
        <Chip size="small" label={label} style={muted ? mutedStyle : baseStyle} />
      </div>
    );
  };

  const colSpan = isProfile ? 6 : 7;

  const tableBody =
    slideList.length > 0 ? (
      slideList.map((banner) => (
        <TableRow key={banner.slider_id} hover>
          <TableCell>{renderImageCell(banner.image_link, "Hero", chip.hero, chip.cmsMuted)}</TableCell>
          <TableCell>{renderImageCell(banner.square_image_link, "CMS", chip.cms, chip.cmsMuted)}</TableCell>
          {!isProfile && (
            <TableCell align="center">
              <span className={classes.posBadge}>{banner.order}</span>
            </TableCell>
          )}
          <TableCell>{banner.title}</TableCell>
          <TableCell className={classes.description}>{banner.description}</TableCell>
          <TableCell align="center">
            <Chip
              clickable
              size="small"
              label={banner.is_active === 1 ? "Active" : "Inactive"}
              onClick={() => handleOpenConfirmModal(banner)}
              style={banner.is_active === 1 ? chip.active : chip.inactive}
            />
          </TableCell>
          <TableCell align="center">
            <IconButton size="small" onClick={() => handleOpenModal("UPDATE", banner)} title="Edit slide">
              <EditIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={colSpan} align="center" style={{ padding: 32, color: "#94a3b8" }}>
          No data
        </TableCell>
      </TableRow>
    );

  const tabBody = (
    <>
      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          inputRef={_searchRef}
          variant="outlined"
          size="small"
          placeholder="Search..."
          onChange={(e) => handleSearchBanner(e.target.value)}
        />
        <FormControl variant="outlined" size="small" className={classes.statusFilter}>
          <InputLabel id="status-filter-label">Filter status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Filter status"
            value={selectedStatus}
            disabled={isRequesting || isRequestingUpsert}
            onChange={handleChangeStatusFilter}
          >
            <MenuItem value={""}>All status</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
      </div>

      {isRequesting ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress style={{ color: "#9cc54b" }} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell}>Hero image</TableCell>
                  <TableCell className={classes.headCell}>CMS image</TableCell>
                  {!isProfile && (
                    <TableCell className={classes.headCell} align="center">
                      Pos
                    </TableCell>
                  )}
                  <TableCell className={classes.headCell}>Title</TableCell>
                  <TableCell className={classes.headCell}>Description</TableCell>
                  <TableCell className={classes.headCell} align="center">
                    Status
                  </TableCell>
                  <TableCell className={classes.headCell} align="center">
                    Edit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{tableBody}</TableBody>
            </Table>
          </TableContainer>

          <div className={classes.pagination}>
            <Button variant="outlined" size="small" disabled={next <= limit} onClick={handlePaginPrev}>
              Prev
            </Button>
            <Typography variant="body2" color="textSecondary">{`${next} / ${sliderCount}`}</Typography>
            <Button variant="outlined" size="small" disabled={next === sliderCount} onClick={handlePaginNext}>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );

  const linkHints = (
    <div className={classes.infoBox}>
      <div className={classes.infoHeader}>
        <InfoIcon style={{ width: 18, marginRight: 6, color: "#1989FA" }} />
        Example redirect to:
      </div>
      {type === 0 ? (
        <>
          <div>
            <b>Brand:</b> {appUrl}/brand/:brandRef
          </div>
          <div>
            <b>Category:</b> {appUrl}/category/:categoryRef
          </div>
          <div>
            <b>Sub category:</b> {appUrl}/category/:categoryRef/:subCategoryRef
          </div>
          <div>
            <b>Search:</b> {appUrl}/search/:keyWord
          </div>
          <div>
            <b>External link:</b> any link
          </div>
        </>
      ) : (
        <>
          <div>
            <b>Supplier:</b> {appUrl}/express/store/:supplierId
          </div>
          <div>
            <b>Search:</b> {appUrl}/express/search/:keyWord
          </div>
          <div>
            <b>External link:</b> any link
          </div>
        </>
      )}
    </div>
  );

  const renderUploadCard = ({ required, ratioLabel, cardTitle, hint, inputRef, onChange, previewUrl, uploading, previewLabel, footnote }) => (
    <Paper variant="outlined" className={classes.uploadCard}>
      <div className={classes.uploadHead}>
        <Box display="flex" style={{ gap: 10 }}>
          <CloudUploadIcon style={{ color: "#2563eb" }} />
          <div>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {cardTitle}
              {required ? " *" : ""}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {hint}
            </Typography>
          </div>
        </Box>
        <Box display="flex" style={{ gap: 6, flexShrink: 0 }}>
          <Chip size="small" label={ratioLabel} style={chip.wide} />
          <Chip size="small" label={required ? "Required" : "Optional"} style={required ? chip.required : chip.optional} />
        </Box>
      </div>

      <input ref={inputRef} type="file" accept="image/*" className={classes.hiddenInput} onChange={onChange} />
      <Button
        variant="outlined"
        size="small"
        startIcon={<CloudUploadIcon />}
        disabled={isRequestingUpsert || uploading}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        Choose image
      </Button>

      <div className={classes.preview}>
        {uploading ? (
          <CircularProgress />
        ) : previewUrl ? (
          <img src={previewUrl} alt={previewLabel} className={classes.previewImg} />
        ) : (
          <Box textAlign="center" style={{ color: "#cbd5e1" }}>
            <PhotoIcon />
            <Typography variant="caption" display="block">
              {previewLabel}
            </Typography>
          </Box>
        )}
      </div>

      <Typography variant="caption" display="block" style={{ color: required ? "#b45309" : "#64748b", marginTop: 8 }}>
        {footnote}
      </Typography>
    </Paper>
  );

  /* ==================== Render ==================== */
  return (
    <div className={classes.page}>
      <Paper className={classes.card} elevation={1}>
        <div className={classes.headerRow}>
          <div>
            <Typography variant="h5" style={{ fontWeight: 700 }}>
              Slide Banners
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              Manage slider banners for homepage, express, and profile
            </Typography>
          </div>
          <Button variant="contained" color="primary" disableElevation disabled={isRequesting || isRequestingUpsert} onClick={() => handleOpenModal("ADD", null)}>
            + Add Slide
          </Button>
        </div>

        <Tabs
          className={classes.tabs}
          value={type}
          indicatorColor="primary"
          textColor="primary"
          onChange={(e, value) => handleTypeChange(value)}
        >
          <Tab value={0} label="Normal" />
          <Tab value={1} label="Express" />
          <Tab value={3} label="Profile Banner" />
        </Tabs>

        {tabBody}
      </Paper>

      {/* ============ Add / Update modal ============ */}
      <Dialog open={isShowModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">{formType === "ADD" ? "Add" : "Update"} Slide</Typography>
          <IconButton size="small" onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Title"
                required
                placeholder="Slide title"
                value={title}
                disabled={isRequestingUpsert}
                error={formError.title}
                helperText={formError.title ? "Title is required." : ""}
                onChange={handleTitleChange}
                style={{ marginBottom: 16 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                label="Description"
                required
                value={description}
                disabled={isRequestingUpsert}
                error={formError.description}
                helperText={formError.description ? "Description is required." : ""}
                onChange={handleDescription}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Slide Link"
                placeholder="https://..."
                value={webLink}
                disabled={isRequestingUpsert}
                error={formError.webLink || webLinkFormatError}
                helperText={
                  formError.webLink
                    ? "Slider link is required."
                    : webLinkFormatError && type === 0
                    ? "Slider link must not have express."
                    : webLinkFormatError
                    ? "Slider link must have express."
                    : ""
                }
                onChange={handleInputChange}
              />
              <FormControlLabel
                control={<Checkbox checked={!!openNewTab} color="primary" onChange={(e) => handleChangeOpenNewTab(e.target.checked)} />}
                label="Open in new tab"
              />
              {formType === "UPDATE" && (
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Slide position"
                  required
                  value={slideNumber}
                  disabled={isRequestingUpsert}
                  error={formError.slideNumber}
                  helperText={formError.slideNumber ? "Slide position is required." : ""}
                  onChange={handleChangeSlideNumber}
                  style={{ marginTop: 8 }}
                />
              )}
              {linkHints}
            </Grid>
          </Grid>

          <Divider style={{ margin: "24px 0 16px" }} />

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} style={{ gap: 8 }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Image Setup
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Upload at least one format. Square is used for the CMS slider cards.
              </Typography>
              {formError.imageUrl && (
                <Typography variant="caption" style={{ color: "#dc2626", display: "block", marginTop: 4 }}>
                  Please upload at least one image (rectangle or square).
                </Typography>
              )}
            </div>
            <Box display="flex" style={{ gap: 6 }}>
              {!imageUrl && !squareImageUrl && <Chip size="small" label="Image required" style={chip.required} />}
              {!squareImageUrl && <Chip size="small" label="CMS hidden" style={chip.optional} />}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {renderUploadCard({
                required: false,
                ratioLabel: "Wide",
                cardTitle: "Rectangle banner",
                hint: "Legacy homepage hero carousel.",
                inputRef: _sliderImageInputFile,
                onChange: handleHeroUpload,
                previewUrl: imageUrl,
                uploading: isUploadingImage,
                previewLabel: "Rectangle preview",
                footnote: imageUrl ? "Hero image set." : "Optional — wide hero banner image.",
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderUploadCard({
                required: false,
                ratioLabel: "1:1",
                cardTitle: "Square image",
                hint: "CMS homepage slider cards.",
                inputRef: _squareImageInputFile,
                onChange: handleSquareUpload,
                previewUrl: squareImageUrl,
                uploading: isUploadingSquareImage,
                previewLabel: "Square preview",
                footnote: squareImageUrl ? "CMS image set." : "Slide will be hidden in CMS until uploaded.",
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={isRequestingUpsert} onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={isRequestingUpsert || isUploadingImage || isUploadingSquareImage}
            onClick={handleUpsertSlider}
            startIcon={isRequestingUpsert ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {formType === "ADD" ? "Add Slide" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ View image modal ============ */}
      <Dialog open={isShowImageModal} onClose={handleCloseImageModal} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Image</Typography>
          <IconButton size="small" onClick={handleCloseImageModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <img alt="Slide" src={imageUrlToView} style={{ width: "100%" }} />
        </DialogContent>
      </Dialog>

      {/* ============ Confirm active/inactive ============ */}
      <Dialog open={isShowConfirmModal} onClose={handleCloseConfirmModal} maxWidth="xs">
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Typography>Do you want to {slideDataToUpdateStatus.is_active ? "inactivate" : "activate"} this slide?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={isRequestingUpdateStatus} onClick={handleCloseConfirmModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            disabled={isRequestingUpdateStatus}
            onClick={handleSubmitDisActive}
            startIcon={isRequestingUpdateStatus ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Toast ============ */}
      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <SnackbarContent
          style={{ backgroundColor: toastSeverity === "success" ? "#16a34a" : "#dc2626" }}
          message={toastMessage}
        />
      </Snackbar>
    </div>
  );
};

export default SlideBanner;
